import React, { useMemo } from "react";
import dayjs from "dayjs";
import Modal from "../../Modal/Modal";
import TableAdv from "../../Table/TableAdv/TableAdv";
import SmartTooltip from "../../Info/SmartTooltip";
import PageTab from "../../DynamicTabs/PageTab";
import { normalizeRange } from "../utils/dateRange";
import {
  buildInternalSubmissionCandidateRows,
  getJobStageCandidateCount,
  getJobStageCandidateCountInRange,
} from "./stageCandidateUtils";

export default function InternalSubmissionModal({
  isOpen,
  onClose,
  data,
  employeeId,
  duration,
  stageName = "Internal Submission",
}) {
  const normalizedRange = useMemo(() => normalizeRange(duration), [duration]);
  const startMs = normalizedRange?.startMs ?? null;
  const endMs = normalizedRange?.endMs ?? null;

  const employeeJobs = useMemo(() => {
    const jobs = data?.jobs && typeof data.jobs === "object" ? data.jobs : {};
    return Object.values(jobs)
      .filter(Boolean)
      .filter((job) => String(job?.bdmId ?? "") === String(employeeId ?? ""));
  }, [data, employeeId]);

  const candidateRows = useMemo(
    () => buildInternalSubmissionCandidateRows(employeeJobs, stageName),
    [employeeJobs, stageName],
  );

  const jobsWith = useMemo(() => {
    const rows = [];
    employeeJobs.forEach((job) => {
      const totalSubmissions = getJobStageCandidateCountInRange(
        job,
        stageName,
        startMs,
        endMs,
      );
      if (totalSubmissions <= 0) return;
      rows.push({
        jobTitle: job?.jobTitle ?? "",
        companyName: job?.companyName ?? "",
        totalSubmissions,
        jobOpenDate: job?.jobOpenDate
          ? dayjs(job.jobOpenDate).format("YYYY-MM-DD")
          : "",
      });
    });
    rows.sort((a, b) => {
      const at = dayjs(a.jobOpenDate).valueOf() || 0;
      const bt = dayjs(b.jobOpenDate).valueOf() || 0;
      if (bt !== at) return bt - at;
      return String(a.jobTitle || "").localeCompare(String(b.jobTitle || ""));
    });
    return rows;
  }, [employeeJobs, endMs, stageName, startMs]);

  const jobsWithout = useMemo(() => {
    const rows = [];
    employeeJobs.forEach((job) => {
      const totalSubmissions = getJobStageCandidateCountInRange(
        job,
        stageName,
        startMs,
        endMs,
      );
      if (totalSubmissions > 0) return;
      rows.push({
        jobTitle: job?.jobTitle ?? "",
        companyName: job?.companyName ?? "",
        jobOpenDate: job?.jobOpenDate
          ? dayjs(job.jobOpenDate).format("YYYY-MM-DD")
          : "",
      });
    });
    rows.sort((a, b) => {
      const at = dayjs(a.jobOpenDate).valueOf() || 0;
      const bt = dayjs(b.jobOpenDate).valueOf() || 0;
      if (bt !== at) return bt - at;
      return String(a.jobTitle || "").localeCompare(String(b.jobTitle || ""));
    });
    return rows;
  }, [employeeJobs, endMs, stageName, startMs]);

  const candidateColumns = useMemo(
    () => [
      { key: "candidateId", label: "Candidate Id", width: "min-w-[220px]!" },
      {
        key: "submittedDate",
        label: "Submitted Date",
        width: "min-w-[160px]!",
      },
      {
        key: "jobTitle",
        label: "Job Title",
        width: "min-w-[260px]! max-w-[340px]!",
        render: (row) => (
          <SmartTooltip
            description={row?.jobTitle}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal={true}
          >
            <div className="line-clamp-1">{row?.jobTitle}</div>
          </SmartTooltip>
        ),
      },
      {
        key: "companyName",
        label: "Company",
        width: "min-w-[220px]! max-w-[280px]!",
        render: (row) => (
          <SmartTooltip
            description={row?.companyName}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal={true}
          >
            <div className="line-clamp-1">{row?.companyName}</div>
          </SmartTooltip>
        ),
      },
    ],
    [],
  );

  const jobWithColumns = useMemo(
    () => [
      {
        key: "jobTitle",
        label: "Job Title",
        width: "min-w-[260px]! max-w-[340px]!",
        render: (row) => (
          <SmartTooltip
            description={row?.jobTitle}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal={true}
          >
            <div className="line-clamp-1">{row?.jobTitle}</div>
          </SmartTooltip>
        ),
      },
      {
        key: "companyName",
        label: "Company",
        width: "min-w-[220px]! max-w-[280px]!",
        render: (row) => (
          <SmartTooltip
            description={row?.companyName}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal={true}
          >
            <div className="line-clamp-1">{row?.companyName}</div>
          </SmartTooltip>
        ),
      },
      {
        key: "totalSubmissions",
        label: "Total Submissions",
        width: "min-w-[180px]!",
      },
    ],
    [],
  );

  const jobWithoutColumns = useMemo(
    () => [
      {
        key: "jobTitle",
        label: "Job Title",
        width: "min-w-[260px]! max-w-[340px]!",
        render: (row) => (
          <SmartTooltip
            description={row?.jobTitle}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal={true}
          >
            <div className="line-clamp-1">{row?.jobTitle}</div>
          </SmartTooltip>
        ),
      },
      {
        key: "companyName",
        label: "Company",
        width: "min-w-[220px]! max-w-[280px]!",
        render: (row) => (
          <SmartTooltip
            description={row?.companyName}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal={true}
          >
            <div className="line-clamp-1">{row?.companyName}</div>
          </SmartTooltip>
        ),
      },
    ],
    [],
  );

  const tabs = useMemo(
    () => [
      {
        title: `Internal Submissions (${candidateRows.length})`,
        type: "main",
        page: (
          <div className="h-full overflow-hidden flex flex-col p-0.5">
            <TableAdv
              showSlNo
              columns={candidateColumns}
              rows={candidateRows}
              tableId="treeHierarchyInternalCandidates"
              toolbarRight={{
                search: true,
                filter: false,
                download: false,
                viewToggle: false,
                fullScreen: false,
                add: [],
              }}
              toolbarLeft={{ add: [] }}
            />
          </div>
        ),
      },
      {
        title: `Jobs with Internal Submission (${jobsWith.length})`,
        type: "main",
        page: (
          <div className="h-full overflow-hidden flex flex-col p-0.5">
            <TableAdv
              columns={jobWithColumns}
              rows={jobsWith}
              tableId="treeHierarchyInternalJobsWith"
              toolbarRight={{
                search: true,
                filter: false,
                download: false,
                viewToggle: false,
                fullScreen: false,
                add: [],
              }}
              toolbarLeft={{ add: [] }}
            />
          </div>
        ),
      },
      {
        title: `Jobs without Internal Submission (${jobsWithout.length})`,
        type: "main",
        page: (
          <div className="h-full overflow-hidden flex flex-col p-0.5">
            <TableAdv
              columns={jobWithoutColumns}
              rows={jobsWithout}
              tableId="treeHierarchyInternalJobsWithout"
              toolbarRight={{
                search: true,
                filter: false,
                download: false,
                viewToggle: false,
                fullScreen: false,
                add: [],
              }}
              toolbarLeft={{ add: [] }}
            />
          </div>
        ),
      },
    ],
    [
      candidateColumns,
      candidateRows,
      jobWithColumns,
      jobWithoutColumns,
      jobsWith,
      jobsWithout,
    ],
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Internal Submission"
      width="max-w-6xl"
      bodyClassName="p-0 flex flex-1 overflow-hidden"
    >
      <div className="w-full max-h-[70vh] overflow-hidden p-2 flex flex-1 ">
        <PageTab
          tabs={tabs}
          defaultTab={0}
          variant="text"
          activeVariant="contained"
          color="black"
          activeColor="primary"
        />
      </div>
    </Modal>
  );
}
